import React, { lazy, type RefObject, Suspense } from 'react';

import Loadable from 'react-loadable';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { ConversationAssistantLoadingContext } from './ConversationAssistantLoadingContext';
import { importConversationAssistant } from './import-conversation-assistant';
import type { ConversationAssistantProps } from './types';
import { MiniModalLoadingFallback } from './ui/mini-modal-fallback/MiniModalLoadingFallback';

type ConversationAssistantAsyncProps = ConversationAssistantProps & {
	isNav4Enabled?: boolean;
	stagingAreaRef?: RefObject<HTMLDivElement>;
	skipRelayProvider?: boolean;
};

const conversationAssistantLazy = lazy(() =>
	importConversationAssistant().then((module) => ({
		default: module.ConversationAssistant,
	})),
);
const LoadingComponent = () => {
	return React.createElement(UFOLoadHold, {
		name: 'LoadingConversationAssistantBundle',
	});
};

const conversationAssistantLoadable = Loadable({
	loader: () => importConversationAssistant().then((m) => m.ConversationAssistant),
	loading: LoadingComponent,
});

const AsyncConversationAssistant: ((
	props: ConversationAssistantAsyncProps,
) => React.JSX.Element) & {
	preload: () => void;
} = Object.assign(
	(props: ConversationAssistantAsyncProps): React.JSX.Element => {
		if (
			props.modeConfig?.mode === 'mini-modal' &&
			isExperimentEnabled('rovo_chat_mini_modal_stable_open') &&
			!(props.skipChatUI ?? Boolean(props.overridingChildren))
		) {
			// Own both bundle-loading stages here so the same fallback stays mounted throughout.
			return React.createElement(
				Suspense,
				{
					fallback: React.createElement(
						UFOLoadHold,
						{ name: 'LoadingConversationAssistantBundle' },
						React.createElement(MiniModalLoadingFallback, {
							topOffset: props.topOffset ?? 80,
							isNav4Enabled: props.isNav4Enabled ?? false,
						}),
					),
				},
				React.createElement(
					ConversationAssistantLoadingContext.Provider,
					{ value: true },
					React.createElement(conversationAssistantLazy, props),
				),
			);
		}

		if (fg('aimate_conversation_assistant_async_loading')) {
			return React.createElement(
				Suspense,
				{ fallback: React.createElement(LoadingComponent) },
				React.createElement(conversationAssistantLazy, props),
			);
		}

		if (props.useChildrenAsLoadingFallback && props.overridingChildren) {
			return React.createElement(
				Suspense,
				{ fallback: props.overridingChildren },
				React.createElement(conversationAssistantLazy, props),
			);
		}

		return React.createElement(conversationAssistantLoadable, props);
	},
	{
		preload: (): void => {
			// React.lazy has no preload API, so warm the shared chunk directly on the R19
			// lazy path; keep react-loadable's preload (which also marks it loaded) otherwise.
			if (fg('aimate_conversation_assistant_async_loading')) {
				void importConversationAssistant();
			} else {
				void conversationAssistantLoadable.preload();
			}
		},
	},
);

export default AsyncConversationAssistant;
